import apiClient from '@/lib/api-client';
import { useSimulationStore } from '@/stores/simulationStore';
import { triggerBlobDownload } from '@/features/media/media.api';
import type { SubmitProbleem } from './submit-foutmelding';

/**
 * spec-klantsimulatie-aanmeldingen: een medewerker die "Bekijk als klant" gebruikt, zegt bij elke
 * aanroep bij wie hij is. Zonder klantnummer weigert de achterkant een interne rol. Buiten simulatie
 * gaat er niets extra's mee.
 */
function klantnummer(): { gln?: string } {
  const { isSimulating, simulatedGln } = useSimulationStore.getState();
  return isSimulating && simulatedGln ? { gln: simulatedGln } : {};
}

export type IntakeSessionFlow = 'product' | 'packaging' | 'marketing';
export type IntakeSessionStatus = 'uploaded' | 'interpreting' | 'review' | 'submitting' | 'submitted' | 'expired';

/** spec-klantsimulatie-aanmeldingen CAP-4: wie een upload of indiening deed. Alleen voor medewerkers. */
export interface IntakeVastleggingRegel {
  handeling: 'upload' | 'indienen';
  medewerkerId: string;
  rol: string;
  klantnummer: string;
  tijdstip: string;
}

export interface IntakeSession {
  id: string;
  gln: string;
  flow: IntakeSessionFlow;
  status: IntakeSessionStatus;
  version: number;
  originalFilename: string;
  rowCount: number;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
  /** ISO timestamp of the sliding-retention cutoff (last activity + 30 days). */
  expiresAt: string;
  /** True from day 23 of inactivity onward (FR36). */
  expiryWarning: boolean;
  /** h5-3 AD-7: true when a container restart interrupted an in-progress interpretation. */
  interrupted: boolean;
  /** h5-3: per-column mapping proposals, once interpretation has completed at least once. */
  mappingProposal: IntakeColumnMappingProposal[] | null;
  /** h5-6 FR34: set when the most recent interpretation run had to use the rule-based fallback. */
  degradationReason: IntakeDegradationReason | null;
  /** h5-8 FR38a: the customer's per-GTIN exclude/proceed choices for detected existing products. */
  productDecisions: ProductDecisionEntry[];
  /** h13-5: naar welke afnemers de producten gaan, per doelmarkt. Leeg tot er een keuze is. */
  retailerChoice: RetailerChoiceEntry[];
  /** h13-5: de doelmarkten die in het aangeleverde bestand voorkomen, als driecijferige codes. */
  targetMarkets: string[];
  /**
   * h6-3 FR40: het ordernummer van een geslaagde indiening. Zolang dit leeg is, is er niets
   * ingediend — ook niet half.
   */
  orderNumber: string | null;
  /** Wanneer de keten is afgerond. */
  submittedAt: string | null;
  /** Waarom de laatste indienpoging strandde, als i18n-sleutel; nooit een tekst van elders (FR48). */
  submitErrorCode: string | null;
  /**
   * h7-4: artikelen die bij déze indiening wel zijn opgeslagen maar niet gepubliceerd.
   *
   * Komt alleen mee in het antwoord op het indienen zelf; het staat nergens opgeslagen. De
   * indiening is gelukt — opnieuw indienen zou de gegevens dubbel opslaan.
   */
  submitWarnings?: SubmitProbleem[];
  /**
   * spec-aanmeldketen-sneller CAP-4: hoeveel producten al zijn afgehandeld, zolang de API de
   * aanmelding afrondt. Ontbreekt na een herstart van de API; dan toont het scherm geen aantal.
   */
  submitProgress?: { handled: number; total: number } | null;
  /** Alleen voor een medewerker: de vastlegging van upload en indiening. De klant krijgt dit veld niet. */
  vastlegging?: IntakeVastleggingRegel[];
}

export type IntakeDegradationReason = 'ai_unavailable' | 'cost_limit';

export type IntakeConfidenceClass = 'high' | 'medium' | 'low';

export interface IntakeColumnMappingProposal {
  header: string;
  suggestedField: string | null;
  /** h5-7 FR35: 'profile' is a remembered per-GLN customer mapping, applied ahead of exact/synonym. */
  source: 'exact' | 'synonym' | 'pattern' | 'ai' | 'profile' | 'none';
  rawConfidence: number;
  /** h5-4 FR30: recalibrated score in [0, 100]. */
  confidenceScore: number;
  /** h5-4 FR30 three-class model — drives the review-screen behaviour built in h5-5. */
  confidenceClass: IntakeConfidenceClass;
  confirmed: boolean;
  /** h5-5 FR31: true when the customer explicitly chose "do not import" for this column. */
  ignored: boolean;
  /** h5-5: anonymized sample values (PiiSanitizer, h5-2) — never raw cell values. */
  sanitizedSampleValues: string[];
}

export interface Gs1FieldOption {
  name: string;
  label: string;
  required: boolean;
}

export type IntakeColumnAction = 'setField' | 'confirm' | 'ignore';

export interface UpdateIntakeColumnInput {
  header: string;
  /** De positie van de kolom; de kop alleen is geen sleutel zodra hij vaker voorkomt. */
  columnIndex?: number;
  expectedVersion: number;
  action: IntakeColumnAction;
  suggestedField?: string | null;
}

/**
 * h7-1: de verpakkingsmeldingen dragen dezelfde vorm als de kolomcontrole. Ze zijn taal-neutraal —
 * het rijnummer, de groep en de veldnaam staan in `params`, de tekst komt uit de vertaling.
 */
export type PackagingValidationErrorCode =
  | 'packagingRequired'
  | 'packagingRequiredIf'
  | 'packagingRequiredWith'
  | 'packagingMustBeInteger'
  | 'packagingMustBeNumber'
  | 'packagingNotInList'
  | 'packagingProhibited'
  | 'packagingTooManyComposites'
  | 'packagingTooFewCompositeLayers'
  | 'packagingCompositeUnknownGtin'
  | 'packagingSupCategoryLapsed'
  | 'packagingSupCategoryRenamed'
  | 'packagingInvalidGtin'
  | 'packagingNoMaterials';

/**
 * h7-2: de marketingmeldingen. Zelfde vorm, maar zonder `params.portalCode` — de bron kent geen
 * nummercodes. Het rijnummer staat in `params.row`; een groep is er niet, want marketing is één rij
 * per artikel.
 */
export type MarketingValidationErrorCode =
  | 'marketingMissingGtin'
  | 'marketingInvalidGtin'
  | 'marketingDuplicateGtin'
  | 'marketingUnknownGtin'
  | 'marketingMessageTooLong'
  | 'marketingUspTooLong'
  | 'marketingKeywordTooLong'
  | 'marketingDescriptionTooLong'
  /**
   * h7-3, en als enige van deze lijst een waarschuwing: onze bestaanscontrole kon niet draaien.
   * Bij CustomerPortal is diezelfde controle onvoorwaardelijk en alles-of-niets, dus de klant moet
   * weten dat zijn aanlevering daar alsnog op kan afketsen — maar hij mag er niet op vastlopen.
   */
  | 'marketingExistenceUnchecked';

export interface ColumnValidationError {
  code:
    | 'duplicateFieldMapping'
    | 'missingGtinMapping'
    | 'invalidGtinCheckDigit'
    | 'missingTargetMarketMapping'
    | PackagingValidationErrorCode
    | MarketingValidationErrorCode;
  /** Een waarschuwing wordt getoond maar houdt het indienen niet tegen. */
  level?: 'warning';
  headers?: string[];
  field?: string;
  params?: Record<string, string | number>;
}

export interface IntakeApiErrorPayload {
  message: string;
  code?: string;
}

/** spec-cp-aanmeldingen-in-lijst: de status van een lijstrij. `notCompleted` komt van CustomerPortal. */
export type IntakeListStatus = IntakeSessionStatus | 'received' | 'notCompleted';

/**
 * Eén regel op Aanmeldingen: een portaalsessie, een aanmelding uit CustomerPortal, of beide samen.
 * Zonder sessie zijn bestandsnaam, rijen en laatste activiteit `null`.
 */
export interface IntakeListRow {
  /** `order:<nr>` (product), `packaging:<nr>` (verpakking) of `session:<id>`. */
  key: string;
  sessionId: string | null;
  flow: IntakeSessionFlow;
  status: IntakeListStatus;
  originalFilename: string | null;
  orderNumber: string | null;
  rowCount: number | null;
  productCount: number | null;
  lastActivityAt: string | null;
  submittedAt: string | null;
  /** `null` als de aanmelding is ingediend: die verloopt niet meer. */
  expiresAt: string | null;
  expiryWarning: boolean;
  /** spec-s3-archief-aanmeldingen deel B: het ingestuurde Excelbestand is bewaard en te downloaden. */
  hasFile: boolean;
  /** Verpakkingsrij: `capture` is vastgelegd door XXtract (geen download), `excel` uit het Excelbestand, anders `null`. */
  origin: 'excel' | 'capture' | null;
}

export interface IntakeList {
  data: IntakeListRow[];
  /** `incomplete`: CustomerPortal leverde niet. `truncated`: alleen de 500 nieuwste aanmeldingen. */
  meta: { incomplete: boolean; truncated: boolean };
}

export async function getIntakeList(): Promise<IntakeList> {
  const response = await apiClient.get<IntakeList>('/intake/list', { params: klantnummer() });
  return response.data;
}

/**
 * spec-s3-archief-aanmeldingen deel B: het Excelbestand van een productaanmelding, via de eigen
 * route van het portaal (nooit een opslaglink), bewaard onder de originele naam uit `filename*`.
 */
export async function downloadSubmissionFile(orderNumber: string, flow: IntakeSessionFlow = 'product'): Promise<void> {
  // spec-verpakkingsaanmeldingen-in-lijst CAP-6: een verpakkingsbestand vraagt de server met `flow`;
  // het productverzoek blijft ongewijzigd (geen `flow` in de query).
  const response = await apiClient.get(`/intake/submissions/${encodeURIComponent(orderNumber)}/file`, {
    params: { ...klantnummer(), ...(flow === 'packaging' ? { flow } : {}) },
    responseType: 'blob',
  });
  const header = (response.headers as Record<string, unknown>)['content-disposition'];
  const encoded = typeof header === 'string' ? /filename\*=UTF-8''([^;\s]+)/i.exec(header)?.[1] : undefined;
  let fileName = `aanmelding-${orderNumber}.xlsx`;
  if (encoded) {
    try {
      fileName = decodeURIComponent(encoded);
    } catch {
      // Onleesbare codering: de vaste naam blijft staan.
    }
  }
  triggerBlobDownload(response.data as Blob, fileName);
}

export async function getIntakeSession(sessionId: string): Promise<IntakeSession> {
  const response = await apiClient.get<{ data: IntakeSession }>(`/intake/sessions/${sessionId}`, { params: klantnummer() });
  return response.data.data;
}

export interface CreateIntakeSessionInput {
  flow: IntakeSessionFlow;
  file: File;
}

export async function createIntakeSession(input: CreateIntakeSessionInput): Promise<IntakeSession> {
  const formData = new FormData();
  formData.append('flow', input.flow);
  formData.append('file', input.file);

  const response = await apiClient.post<{ data: IntakeSession }>('/intake/sessions', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params: klantnummer(),
  });
  return response.data.data;
}

/** h5-3 AD-7 retry path: re-triggers interpretation for a session left `interrupted` by boot-recovery. */
export async function retryIntakeInterpretation(sessionId: string): Promise<IntakeSession> {
  const response = await apiClient.post<{ data: IntakeSession }>(`/intake/sessions/${sessionId}/interpretation/retry`, undefined, {
    params: klantnummer(),
  });
  return response.data.data;
}

/**
 * h5-5: the GS1 field reference list for the review screen's searchable target-field dropdown.
 * h7-1: per flow — een verpakkingssessie kiest uit verpakkingsvelden, niet uit productvelden.
 */
export async function getGs1FieldOptions(flow: IntakeSessionFlow = 'product'): Promise<Gs1FieldOption[]> {
  const response = await apiClient.get<{ data: Gs1FieldOption[] }>('/intake/gs1-fields', { params: { flow, ...klantnummer() } });
  return response.data.data;
}

/** h5-5: confirm/change/ignore a single column's mapping, with optimistic-concurrency versioning. */
export async function updateIntakeColumn(sessionId: string, input: UpdateIntakeColumnInput): Promise<IntakeSession> {
  const response = await apiClient.patch<{ data: IntakeSession }>(`/intake/sessions/${sessionId}/columns`, input, { params: klantnummer() });
  return response.data.data;
}

/** h5-5 FR32: read-only structural validation — never mutates the session. */
export async function validateIntakeSession(sessionId: string): Promise<ColumnValidationError[]> {
  const response = await apiClient.get<{ data: { errors: ColumnValidationError[] } }>(`/intake/sessions/${sessionId}/validation`, {
    params: klantnummer(),
  });
  return response.data.data.errors;
}

/** h5-5 FR33: explicit submit confirmation — E5 ends at status `submitting`. */
export async function submitIntakeSession(sessionId: string, expectedVersion: number): Promise<IntakeSession> {
  const response = await apiClient.post<{ data: IntakeSession }>(`/intake/sessions/${sessionId}/submit`, { expectedVersion }, {
    params: klantnummer(),
  });
  return response.data.data;
}

/** h5-7 FR35: one append-only mapping-profile version, for the Beheer version-history view. */
export interface MappingProfileVersion {
  id: string;
  gln: string;
  version: number;
  mappings: { header: string; field: string }[];
  sourceSessionId: string | null;
  resetNote: string | null;
  resetByUserId: string | null;
  createdAt: string;
}

/** h5-7 FR35: Beheer-only — full version history for a GLN, newest (= active) first. */
export async function getMappingProfileVersions(gln: string): Promise<MappingProfileVersion[]> {
  const response = await apiClient.get<{ data: MappingProfileVersion[] }>(`/mapping-profiles/${gln}`);
  return response.data.data;
}

/** h5-7 FR35: Beheer-only reset — always requires an audit note; creates a brand-new empty version. */
export async function resetMappingProfile(gln: string, note: string): Promise<MappingProfileVersion> {
  const response = await apiClient.post<{ data: MappingProfileVersion }>(`/mapping-profiles/${gln}/reset`, { note });
  return response.data.data;
}

/** h5-8 FR38a: the customer's per-GTIN exclude/proceed choice, persisted on the session. */
export type ProductDecision = 'exclude' | 'proceed';

export interface ProductDecisionEntry {
  gtin: string;
  decision: ProductDecision;
}

export type ChangeDetectionSource = 'dqs' | 'local';

export interface ProductFieldDiff {
  field: string;
  fieldLabel: string;
  oldValue: string;
  newValue: string;
}

export interface ProductChangeInfo {
  gtin: string;
  isChange: boolean;
  source: ChangeDetectionSource | null;
  diff: ProductFieldDiff[];
  /** A7-versoberingspad: known change via DQS, but no comparable field-level data — "bestaat al" only. */
  scoreOnly: boolean;
  existingScore: number | null;
}

export interface ChangeDetectionResult {
  /** NFR4: true when the DQS lookup itself failed — the local check may still have run. */
  degraded: boolean;
  products: ProductChangeInfo[];
}

/** h5-8 FR38a: read-only, computed fresh on every call — never mutates the session. */
export async function getIntakeSessionChanges(sessionId: string): Promise<ChangeDetectionResult> {
  const response = await apiClient.get<{ data: ChangeDetectionResult }>(`/intake/sessions/${sessionId}/changes`, { params: klantnummer() });
  return response.data.data;
}

export interface UpdateProductDecisionInput {
  gtin: string;
  decision: ProductDecision;
  expectedVersion: number;
}

export async function updateProductDecision(sessionId: string, input: UpdateProductDecisionInput): Promise<IntakeSession> {
  const response = await apiClient.patch<{ data: IntakeSession }>(
    `/intake/sessions/${sessionId}/products/${input.gtin}`,
    {
      decision: input.decision,
      expectedVersion: input.expectedVersion,
    },
    { params: klantnummer() },
  );
  return response.data.data;
}

/**
 * h6-4 — de status van één ingediende aanmelding, in de taal van de klant.
 *
 * `label` mag `null` zijn: de achterkant geeft geen interne stapcode terug als er niets is
 * geconfigureerd, en het scherm vult er zijn eigen neutrale tekst in via de vertaallaag.
 */
export interface IntakeOrderStatusStep {
  id: string;
  label: string | null;
  timestamp: string | null;
  status: 'complete' | 'current';
  /** Deze stap komt uit onze eigen aanmelding: ingediend, maar nog geen processtappen binnen. */
  ondergrens: boolean;
}

export interface IntakeOrderStatusProduct {
  tradeItemId: string;
  gtin: string;
  steps: IntakeOrderStatusStep[];
  /** Is de tijdlijn van dit product opgehaald? Zo niet, dan zegt het scherm daar niets over. */
  fetched: boolean;
}

export interface IntakeOrderStatus {
  orderNumber: string;
  submittedAt: string | null;
  summary: { id: string; label: string | null; ondergrens: boolean };
  products: IntakeOrderStatusProduct[];
  /** De bron was onbereikbaar; het scherm meldt dat en toont verder wat er wél bekend is. */
  degraded: boolean;
  /** Er zijn meer producten dan dit scherm in één keer ophaalt. */
  truncated: boolean;
  /** spec-cp-aanmeldingen-in-lijst: CustomerPortal telt nog niet afgeronde producten. */
  unfinished: boolean;
}

export async function getIntakeOrderStatus(
  orderNumber: string,
  opties: { language?: string | null; gln?: string | null },
): Promise<IntakeOrderStatus> {
  // Niet blind `startsWith` aanroepen: de taal is er niet altijd al op het moment dat het scherm
  // voor het eerst ophaalt, en een crash in de aanroep zou als "aanmelding niet gevonden" lezen.
  const parameters = new URLSearchParams({ language: opties.language?.startsWith('en') ? 'en' : 'nl' });
  // Een medewerker die meekijkt moet zeggen bij wie: zonder klantnummer weigert de achterkant het
  // verzoek, want een interne rol gaat ongefilterd door de afscherming heen.
  if (opties.gln) parameters.set('gln', opties.gln);

  const response = await apiClient.get<{ data: IntakeOrderStatus }>(
    `/intake/orders/${encodeURIComponent(orderNumber)}?${parameters.toString()}`,
  );
  return response.data.data;
}

/** h13-5: één afnemer zoals de klant hem kiest. De naam rijdt mee zodat de chips hem terugtonen. */
export interface RetailerSelection {
  gln: string;
  name: string;
}

export interface RetailerChoiceEntry {
  targetMarket: string;
  retailers: RetailerSelection[];
  chosenAt: string;
}

/** h13-4: één afnemer uit de bron, met hoe vaak deze klant hem eerder koos. */
export interface RetailerHistoryEntry {
  gln: string;
  name: string;
  /** Stuurt alleen de volgorde en het voorstel; dit getal wordt nooit aan de klant getoond. */
  timesChosen: number;
}

export interface RetailerHistory {
  retailers: RetailerHistoryEntry[];
  /** `false` betekent: bron onbereikbaar. Het scherm toont dan één informatieregel, geen fout. */
  available: boolean;
}

/** h13-4: de afnemers van één doelmarkt. De leverancier komt uit de sessie, nooit uit het verzoek. */
export async function getRetailerHistory(targetMarket: string): Promise<RetailerHistory> {
  const response = await apiClient.get<{ data: RetailerHistory }>(`/intake/retailer-history?targetMarket=${encodeURIComponent(targetMarket)}`, {
    params: klantnummer(),
  });
  return response.data.data;
}

/** H13-8: één markt met zijn eigen lijst. Een afnemer bestaat niet in elk land. */
export interface RetailerChoiceInputEntry {
  targetMarket: string;
  retailers: RetailerSelection[];
}

export interface UpdateRetailerChoiceInput {
  /** Altijd regels, ook bij één markt: één schrijfactie, dus nooit een halve batchkeuze. */
  entries: RetailerChoiceInputEntry[];
  expectedVersion: number;
}

/** h13-5: de volledige keuze voor één doelmarkt; elke vinkje-klik komt hier langs. */
export async function updateRetailerChoice(sessionId: string, input: UpdateRetailerChoiceInput): Promise<IntakeSession> {
  const response = await apiClient.put<{ data: IntakeSession }>(`/intake/sessions/${sessionId}/retailers`, input, { params: klantnummer() });
  return response.data.data;
}
